// src/components/atomic-crm/companies/CompanyShow.tsx
import { formatDistance } from "date-fns";
import {
  UserPlus,
  Receipt,
  Activity,
  Users,
  DollarSign,
} from "lucide-react";
import {
  RecordContextProvider,
  ShowBase,
  useListContext,
  useRecordContext,
  useShowContext,
  useTranslate,
  useLocale,
  useNotify,
  useRedirect,
} from "ra-core";
import { useEffect, useState } from "react";
import {
  Link as RouterLink,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";
import { Button } from "@/components/ds/ui/button";
import { Card, CardContent } from "@/components/ds/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ds/ui/tabs";
import { ReferenceManyField } from "@/components/ds/admin/reference-many-field";
import { SortButton } from "@/components/ds/admin/sort-button";

import { Avatar } from "../contacts/Avatar";
import { TagsList } from "../contacts/TagsList";
import { findDealLabel } from "../deals/deal";
import { Status } from "../misc/Status";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Contact, Deal, Invoice } from "../types";
import { CompanyAvatar } from "./CompanyAvatar";
import { InvoiceCard } from "../invoices";
import { getDateFnsLocale } from "@/i18n/date-fns";
import { ActivityStream } from "../activities/ActivityStream.tsx";

import { useQuery } from "@tanstack/react-query";

import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import { ProfileCard } from "@/components/atomic-crm/profile/ProfileCard";
import { CompanyAside, CompanyAsideActions } from "./CompanyAside";



export const CompanyShow = () => (
  <ShowBase>
    <CompanyShowContent />
  </ShowBase>
);

const CompanyShowContent = () => {
  const { record, isPending, error } = useShowContext<Company>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const notify = useNotify();
  const redirect = useRedirect();

  const companyId = record?.id;

  const [refreshKey, setRefreshKey] = useState(0);
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: profileRow, refetch: refetchProfile } = useQuery({
    queryKey: ["ai_profiles", "company", companyId, refreshKey],
    queryFn: async () => {
      if (!companyId) return null;
      const { data, error } = await supabase
        .from("ai_profiles")
        .select("profile, generated_at, model, regenerated_count")
        .eq("entity_type", "company")
        .eq("entity_id", companyId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!companyId,
  });

  const generateProfile = async (force: boolean) => {
    if (!companyId) return;
    setIsGenerating(true);
    try {
      await supabase.functions.invoke("generate-profile", {
        body: { entity_type: "company", entity_id: companyId, force },
      });
      refetchProfile();
    } finally {
      setIsGenerating(false);
    }
  };


  // Handle error (company not found)
  useEffect(() => {
    if (error) {
      notify("Company not found or has been deleted", { type: "error" });
      redirect("/companies");
    }
  }, [error, notify, redirect]);

  // Get tab from URL or default to "activity"
  const tabMatch = useMatch("/companies/:id/show/:tab");
  const currentTab = tabMatch?.params?.tab || "activity";

  const handleTabChange = (value: string) => {
    if (value === currentTab) return;
    if (value === "activity") {
      navigate(`/companies/${record?.id}/show`);
      return;
    }
    navigate(`/companies/${record?.id}/show/${value}`);
  };

  if (isPending || !record) return null;

  return (
    <div className="mt-2 flex pb-2 gap-8">
      <div className="flex-1 min-w-0">
        <Card>
          <CardContent>
            <div className="flex items-center mb-4 gap-3">
              <CompanyAvatar />
              <h5 className="text-lg sm:text-xl flex-1 min-w-0 truncate">
                {record.name}
              </h5>
              {/* Mobile-only actions — the desktop aside already has these */}
              <div className="sm:hidden">
                <CompanyAsideActions />
              </div>
            </div>

            {/* Profile section */}
            {/* Profile section */}
            <div className="mt-6 mb-6">
              <ProfileCard
                profileRow={profileRow}
                variant="company"
                isGenerating={isGenerating}
                onGenerate={() => generateProfile(true)}
                onRefresh={() => generateProfile(true)}
              />
            </div>

            <Tabs defaultValue={currentTab} onValueChange={handleTabChange}>
              <TabsList className="w-full flex justify-start h-auto p-1 bg-muted/50 gap-1">
                <TabsTrigger value="activity" className="gap-1.5 px-2 sm:px-3">
                  <Activity className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.activity")}
                  </span>
                </TabsTrigger>

                <TabsTrigger value="contacts" className="gap-1.5 px-2 sm:px-3">
                  <Users className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.contacts", {
                      smart_count: record.nb_contacts || 0,
                    })}
                  </span>
                  {record.nb_contacts > 0 && (
                    <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5">
                      {record.nb_contacts}
                    </span>
                  )}
                </TabsTrigger>

                {record.nb_deals ? (
                  <TabsTrigger value="deals" className="gap-1.5 px-2 sm:px-3">
                    <DollarSign className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">
                      {translate("crm.common.deals", {
                        smart_count: record.nb_deals || 0,
                      })}
                    </span>
                    {record.nb_deals > 0 && (
                      <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5">
                        {record.nb_deals}
                      </span>
                    )}
                  </TabsTrigger>
                ) : null}

                <TabsTrigger value="invoices" className="gap-1.5 px-2 sm:px-3">
                  <Receipt className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">
                    {translate("crm.common.invoices", {
                      smart_count: record.nb_invoices || 0,
                    })}
                  </span>
                  {record.nb_invoices > 0 && (
                    <span className="text-[10px] bg-primary/20 text-primary rounded-full px-1.5">
                      {record.nb_invoices}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
              <TabsContent value="activity" className="pt-2">
                <ActivityStream resource="companies" recordId={record.id} record={record} />
              </TabsContent>
              <TabsContent value="contacts">
                {record.nb_contacts ? (
                  <ReferenceManyField
                    reference="contacts_summary"
                    target="company_id"
                    sort={{ field: "last_name", order: "ASC" }}
                  >
                    <div className="flex flex-col gap-4">
                      <div className="flex flex-row justify-end space-x-2 mt-1">
                        {!!record.nb_contacts && (
                          <SortButton
                            fields={["last_name", "first_name", "last_seen"]}
                          />
                        )}
                        <CreateRelatedContactButton />
                      </div>
                      <ContactsIterator />
                    </div>
                  </ReferenceManyField>
                ) : (
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-row justify-end space-x-2 mt-1">
                      <CreateRelatedContactButton />
                    </div>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="deals">
                {record.nb_deals ? (
                  <ReferenceManyField
                    reference="deals"
                    target="company_id"
                    sort={{ field: "name", order: "ASC" }}
                  >
                    <DealsIterator />
                  </ReferenceManyField>
                ) : null}
              </TabsContent>
              <TabsContent value="invoices">
                <ReferenceManyField
                  reference="invoices"
                  target="company_id"
                  sort={{ field: "issue_date", order: "DESC" }}
                >
                  <div className="flex flex-col gap-4 pt-2">
                    <div className="flex flex-row justify-end space-x-2">
                      <CreateRelatedInvoiceButton />
                    </div>
                    <InvoicesIterator />
                  </div>
                </ReferenceManyField>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <div className="hidden md:block">
        <CompanyAside />
      </div>
    </div>
  );
};

const ContactsIterator = () => {
  const location = useLocation();
  const { data: contacts, error, isPending } = useListContext<Contact>();
  const translate = useTranslate();
  const locale = useLocale();

  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div className="pt-0">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <div className="p-0 text-sm">
            <RouterLink
              to={`/contacts/${contact.id}/show`}
              state={{ from: location.pathname }}
              className="flex items-center justify-between hover:bg-muted py-2 transition-colors"
            >
              <div className="mr-4">
                <Avatar />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium">
                  {`${contact.first_name} ${contact.last_name}`}
                </div>
                <div className="text-sm text-muted-foreground">
                  {contact.title}
                  {contact.nb_tasks
                    ? ` - ${translate("crm.task.field.task", {
                        count: contact.nb_tasks,
                        smart_count: contact.nb_tasks,
                      })}`
                    : ""}
                  &nbsp; &nbsp;
                  <TagsList />
                </div>
              </div>
              {contact.last_seen && (
                <div className="text-right">
                  <div className="text-sm text-muted-foreground">
                    {translate("crm.common.last_activity", {
                      distance: formatDistance(contact.last_seen, now, {
                        locale: getDateFnsLocale(locale),
                      }),
                    })}{" "}
                    <Status status={contact.status} />
                  </div>
                </div>
              )}
            </RouterLink>
          </div>
        </RecordContextProvider>
      ))}
    </div>
  );
};

const CreateRelatedContactButton = () => {
  const company = useRecordContext<Company>();
  const translate = useTranslate();
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/contacts/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <UserPlus className="h-4 w-4" />
        {translate("crm.common.add_contact")}
      </RouterLink>
    </Button>
  );
};

const DealsIterator = () => {
  const { data: deals, error, isPending } = useListContext<Deal>();
  const { dealStages } = useConfigurationContext();
  const translate = useTranslate();
  const locale = useLocale();

  if (isPending || error) return null;

  const now = Date.now();
  return (
    <div>
      <div>
        {deals.map((deal) => (
          <div key={deal.id} className="p-0 text-sm">
            <RouterLink
              to={`/deals/${deal.id}/show`}
              className="flex items-center justify-between hover:bg-muted py-2 px-4 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium">{deal.name}</div>
                <div className="text-sm text-muted-foreground">
                  {findDealLabel(dealStages, deal.stage)},{" "}
                  {deal.amount.toLocaleString(locale, {
                    notation: "compact",
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "narrowSymbol",
                    minimumSignificantDigits: 3,
                  })}
                  {deal.category ? `, ${deal.category}` : ""}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">
                  {translate("crm.common.last_activity", {
                    distance: formatDistance(deal.updated_at, now, {
                      locale: getDateFnsLocale(locale),
                    }),
                  })}{" "}
                </div>
              </div>
            </RouterLink>
          </div>
        ))}
      </div>
    </div>
  );
};

const InvoicesIterator = () => {
  const { data: invoices, error, isPending } = useListContext<Invoice>();
  if (isPending || error) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
      {invoices.map((invoice) => (
        <RouterLink key={invoice.id} to={`/invoices/${invoice.id}/show`}>
          <InvoiceCard invoice={invoice} />
        </RouterLink>
      ))}
    </div>
  );
};

const CreateRelatedInvoiceButton = () => {
  const company = useRecordContext<Company>();
  const translate = useTranslate();
  return (
    <Button variant="outline" asChild size="sm" className="h-9">
      <RouterLink
        to="/invoices/create"
        state={company ? { record: { company_id: company.id } } : undefined}
        className="flex items-center gap-2"
      >
        <Receipt className="h-4 w-4" />
        {translate("crm.common.add_invoice")}
      </RouterLink>
    </Button>
  );
};
